import {
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from "react";
import { fa } from "../i18n/fa";
import { renderAvatarCrop, type AvatarCropRect } from "./avatar-upload";
import { Button } from "./Button";
import { Sheet } from "./social-ui";

/* ------------------------------------------------------------------------- *
 * Pure crop geometry (exported for unit tests — no DOM below this banner).
 *
 * Model: a fixed square FRAME of side `frame` (screen px) sits centred on the
 * gesture surface, and the image is drawn centred on the same point, scaled by
 * `displayScale = coverScale × zoom` and shifted by the pan `(x, y)` — the
 * image-centre's offset from the frame centre, in screen px.
 *
 *  - zoom 1 = "cover": the image's SHORT side exactly fills the frame, so the
 *    initial view IS the server's center-crop; zoom only goes IN, up to
 *    {@link CROP_MAX_ZOOM}× cover. Below cover would letterbox — forbidden.
 *  - {@link clampPan} keeps the frame fully covered: |x| ≤ (w·s − frame)/2
 *    (same for y), so a blank edge can never enter the frame.
 *  - {@link cropRect} maps the frame back to SOURCE pixels: a square of side
 *    frame/s centred on (w/2 − x/s, h/2 − y/s) — the exact area «ذخیره»
 *    renders to the 512×512 avatar.
 * ------------------------------------------------------------------------- */

/** Natural (source) size of the picked image, in source px. */
export interface CropImage {
  width: number;
  height: number;
}

/** A point in screen px, relative to the frame centre. */
export interface CropPoint {
  x: number;
  y: number;
}

/** Pan/zoom of the image within the frame (see the geometry banner above). */
export interface CropTransform {
  zoom: number;
  x: number;
  y: number;
}

/** zoom 1 = cover — the minimum that still fills the frame. */
export const CROP_MIN_ZOOM = 1;
/** Maximum magnification, relative to cover. */
export const CROP_MAX_ZOOM = 4;

function clampNumber(value: number, lo: number, hi: number): number {
  const v = Math.min(hi, Math.max(lo, value));
  return v === 0 ? 0 : v; // normalize -0 (a negative pan clamped to a zero-slack axis)
}

/** Zoom is only ever [cover … CROP_MAX_ZOOM×cover]. */
export function clampZoom(zoom: number): number {
  return clampNumber(zoom, CROP_MIN_ZOOM, CROP_MAX_ZOOM);
}

/** Scale at which the image's short side exactly fills the frame (zoom 1). */
export function coverScale(image: CropImage, frame: number): number {
  return frame / Math.max(1, Math.min(image.width, image.height));
}

/** Screen px per source px at a given zoom. */
export function displayScale(image: CropImage, frame: number, zoom: number): number {
  return coverScale(image, frame) * clampZoom(zoom);
}

/** Clamp a pan so the image always fully covers the frame (no blank edges). */
export function clampPan(
  image: CropImage,
  frame: number,
  zoom: number,
  x: number,
  y: number,
): CropPoint {
  const s = displayScale(image, frame, zoom);
  const maxX = Math.max(0, (image.width * s - frame) / 2);
  const maxY = Math.max(0, (image.height * s - frame) / 2);
  return { x: clampNumber(x, -maxX, maxX), y: clampNumber(y, -maxY, maxY) };
}

/** Drag by (dx, dy) screen px, clamped to keep the frame covered. */
export function panBy(
  image: CropImage,
  frame: number,
  t: CropTransform,
  dx: number,
  dy: number,
): CropTransform {
  const zoom = clampZoom(t.zoom);
  return { zoom, ...clampPan(image, frame, zoom, t.x + dx, t.y + dy) };
}

/**
 * Zoom to `zoom`, keeping the source point under `focal` (screen px relative to
 * the frame centre) fixed — the pinch/wheel anchor — then re-clamp the pan so
 * zooming OUT near an edge pulls the image back over the frame.
 */
export function zoomTo(
  image: CropImage,
  frame: number,
  t: CropTransform,
  zoom: number,
  focal: CropPoint,
): CropTransform {
  const next = clampZoom(zoom);
  const ratio = displayScale(image, frame, next) / displayScale(image, frame, t.zoom);
  const x = focal.x - (focal.x - t.x) * ratio;
  const y = focal.y - (focal.y - t.y) * ratio;
  return { zoom: next, ...clampPan(image, frame, next, x, y) };
}

/** The square of SOURCE pixels the frame is showing — what «ذخیره» renders. */
export function cropRect(image: CropImage, frame: number, t: CropTransform): AvatarCropRect {
  const s = displayScale(image, frame, t.zoom);
  const size = Math.min(frame / s, Math.min(image.width, image.height));
  const cx = image.width / 2 - t.x / s;
  const cy = image.height / 2 - t.y / s;
  return {
    x: clampNumber(cx - size / 2, 0, image.width - size),
    y: clampNumber(cy - size / 2, 0, image.height - size),
    size,
  };
}

/** Square frame side for a viewport: as large as fits the sheet's column while
 * leaving room for the title/slider/buttons (~260px) — clamped to [160, 340]. */
export function frameSizeFor(viewportWidth: number, viewportHeight: number): number {
  const fit = Math.floor(Math.min(viewportWidth - 32, viewportHeight - 260));
  return Math.max(160, Math.min(340, fit));
}

/* ------------------------------------------------------------------------- *
 * The component
 * ------------------------------------------------------------------------- */

function relativeToCenter(el: HTMLElement, clientX: number, clientY: number): CropPoint {
  const r = el.getBoundingClientRect();
  return { x: clientX - (r.left + r.width / 2), y: clientY - (r.top + r.height / 2) };
}

/**
 * The «برش عکس پروفایل» sheet: the picked image inside a fixed square frame —
 * drag to pan, pinch/wheel/slider to zoom (all clamped so the frame is always
 * fully covered) — and «ذخیره» renders exactly the framed square to a 512×512
 * JPEG. RTL-safe (all math is in physical px), dependency-free.
 */
export function AvatarCropper({
  src,
  onSave,
  onCancel,
}: {
  /** Object URL of the picked (already validated/transcoded) image. */
  src: string;
  /** Receives the framed 512×512 JPEG — or null when the platform cannot render
   *  a crop; the caller then uploads the original and the server's center-crop
   *  still applies. */
  onSave: (cropped: Blob | null) => void;
  onCancel: () => void;
}): ReactNode {
  const [image, setImage] = useState<CropImage | null>(null);
  /** The pick would not decode HERE. Distinct from `!image`, which also covers "dimensions have
   *  not arrived yet" — that case still has the server-side fallback and must stay saveable. */
  const [loadFailed, setLoadFailed] = useState(false);
  const [transform, setTransform] = useState<CropTransform>({ zoom: 1, x: 0, y: 0 });
  const [frame, setFrame] = useState(() => frameSizeFor(window.innerWidth, window.innerHeight));
  const [saving, setSaving] = useState(false);
  const surfaceRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const pointers = useRef(new Map<number, CropPoint>());
  const savingRef = useRef(false);

  // Track viewport changes (rotation / Telegram viewport growth)…
  useEffect(() => {
    const onResize = (): void => setFrame(frameSizeFor(window.innerWidth, window.innerHeight));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // …and re-clamp the pan whenever the frame or image changes.
  useEffect(() => {
    if (!image) return;
    setTransform((t) => ({ ...t, ...clampPan(image, frame, t.zoom, t.x, t.y) }));
  }, [image, frame]);

  // Wheel zoom needs a NATIVE non-passive listener: React's root-delegated wheel
  // handler is passive, so preventDefault (required to stop page scroll) is inert.
  useEffect(() => {
    const el = surfaceRef.current;
    if (!el || !image) return;
    const onWheel = (e: WheelEvent): void => {
      e.preventDefault();
      const focal = relativeToCenter(el, e.clientX, e.clientY);
      setTransform((t) => zoomTo(image, frame, t, t.zoom * Math.exp(-e.deltaY * 0.0015), focal));
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [image, frame]);

  /**
   * The `<img>` refused the blob — a mislabelled HEIC, or a photo past this WebView's decoder.
   * Without this handler `onImageLoad` simply never fires, `image` stays null and the sheet renders
   * a black box FOREVER with no message: the exact dead end that left 60 users unable to set a
   * photo. Say so, and block the save so a pick we cannot even render is never uploaded.
   */
  function onImageError(): void {
    setLoadFailed(true);
  }

  function onImageLoad(): void {
    setLoadFailed(false); // a later successful load clears an earlier failure
    const el = imgRef.current;
    if (el && el.naturalWidth > 0) {
      // `naturalWidth/Height` are the EXIF-oriented dimensions in modern
      // WebViews — the same space the <img> displays and drawImage samples,
      // so preview, geometry and output all agree on which way is up.
      setImage({ width: el.naturalWidth, height: el.naturalHeight });
      setTransform({ zoom: 1, x: 0, y: 0 });
    }
  }

  function onPointerDown(e: ReactPointerEvent<HTMLDivElement>): void {
    // Guarded: happy-dom (tests) has no pointer capture.
    e.currentTarget.setPointerCapture?.(e.pointerId);
    pointers.current.set(e.pointerId, relativeToCenter(e.currentTarget, e.clientX, e.clientY));
  }

  function onPointerMove(e: ReactPointerEvent<HTMLDivElement>): void {
    const prev = pointers.current.get(e.pointerId);
    if (!prev) return; // not a drag we started
    const next = relativeToCenter(e.currentTarget, e.clientX, e.clientY);
    pointers.current.set(e.pointerId, next);
    if (!image) return;

    const other = [...pointers.current.entries()].find(([id]) => id !== e.pointerId)?.[1];
    if (other) {
      // Pinch: zoom about the pair's previous midpoint by the distance ratio,
      // then pan by the midpoint's travel (each step re-clamped).
      const d0 = Math.hypot(prev.x - other.x, prev.y - other.y);
      const d1 = Math.hypot(next.x - other.x, next.y - other.y);
      const mid0 = { x: (prev.x + other.x) / 2, y: (prev.y + other.y) / 2 };
      const mid1 = { x: (next.x + other.x) / 2, y: (next.y + other.y) / 2 };
      setTransform((t) => {
        const zoomed = d0 > 0 ? zoomTo(image, frame, t, (t.zoom * d1) / d0, mid0) : t;
        return panBy(image, frame, zoomed, mid1.x - mid0.x, mid1.y - mid0.y);
      });
    } else {
      setTransform((t) => panBy(image, frame, t, next.x - prev.x, next.y - prev.y));
    }
  }

  function onPointerEnd(e: ReactPointerEvent<HTMLDivElement>): void {
    pointers.current.delete(e.pointerId);
  }

  async function handleSave(): Promise<void> {
    // Ref guard: two taps in the same frame both see stale state, so `saving`
    // alone can't stop a double onSave (→ double upload).
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    try {
      const el = imgRef.current;
      if (!image || !el) {
        // Dimensions never arrived (ancient WebView) — hand back null; the caller
        // uploads the original and the server's center-crop is the safety net.
        onSave(null);
        return;
      }
      onSave(await renderAvatarCrop(el, cropRect(image, frame, transform)));
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  }

  const scale = image ? displayScale(image, frame, transform.zoom) : 1;
  const dispW = image ? image.width * scale : 0;
  const dispH = image ? image.height * scale : 0;

  return (
    <Sheet label={fa.profile.cropTitle} onClose={onCancel}>
      <h3 className="m-0 mb-md text-center font-headline-sm text-headline-sm text-on-surface">
        {fa.profile.cropTitle}
      </h3>

      {/* Gesture surface: drag to pan, pinch/wheel to zoom. */}
      <div
        ref={surfaceRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerEnd}
        onPointerCancel={onPointerEnd}
        onPointerLeave={onPointerEnd}
        className="relative w-full select-none overflow-hidden rounded-xl bg-black/70"
        style={{ height: frame + 24, touchAction: "none", cursor: "grab" }}
      >
        <img
          ref={imgRef}
          src={src}
          alt=""
          draggable={false}
          onLoad={onImageLoad}
          onError={onImageError}
          className="pointer-events-none absolute left-1/2 top-1/2"
          style={
            image
              ? {
                  width: dispW,
                  height: dispH,
                  maxWidth: "none",
                  transform: `translate(${transform.x - dispW / 2}px, ${transform.y - dispH / 2}px)`,
                }
              : { visibility: "hidden" }
          }
        />
        {/* The fixed square that becomes the avatar: dim everything outside it,
            and hint the final ROUND display with the inscribed circle. */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-lg border-2 border-secondary"
          style={{ width: frame, height: frame, boxShadow: "0 0 0 9999px rgba(0,0,0,0.6)" }}
        >
          <div className="absolute inset-0 rounded-full border border-white/40" />
        </div>
      </div>

      {/* Zoom slider — the accessible fallback for pinch/wheel. */}
      <div className="mt-md flex items-center gap-sm">
        <span className="font-label-md text-label-md text-on-surface-variant">
          {fa.profile.cropZoom}
        </span>
        <input
          type="range"
          min={CROP_MIN_ZOOM}
          max={CROP_MAX_ZOOM}
          step={0.01}
          value={transform.zoom}
          disabled={!image}
          aria-label={fa.profile.cropZoom}
          onChange={(e) => {
            const zoom = Number(e.target.value);
            if (image) setTransform((t) => zoomTo(image, frame, t, zoom, { x: 0, y: 0 }));
          }}
          className="h-1 flex-1 cursor-pointer accent-secondary"
        />
      </div>
      <p
        className={`m-0 mt-xs text-center font-caption text-caption ${
          loadFailed ? "text-error" : "text-on-surface-variant"
        }`}
        {...(loadFailed ? { role: "alert" as const } : {})}
      >
        {loadFailed ? fa.profile.cropLoadFailed : fa.profile.cropHint}
      </p>

      <div className="mt-md flex flex-col gap-sm">
        <Button variant="primary" disabled={saving || loadFailed} onClick={() => void handleSave()}>
          {fa.profile.cropSave}
        </Button>
        <Button variant="ghost" onClick={onCancel}>
          {fa.profile.cropCancel}
        </Button>
      </div>
    </Sheet>
  );
}
